import React, { useState } from 'react';
import { connect } from 'react-redux';
import { useLocale } from 'locale';
import Logo from 'components/navigation/Logo';
import SearchThinIcon from 'assets/svg/investors-mobile-header-search.svg';
import UserIcon from 'assets/svg/investors-mobile-header-user.svg';
import { Route } from 'config/routes';
import { ShowAt } from 'consts/breakpoints';
import { State } from 'store/state';
import { routeNameSelector } from 'store/state/selectors/router';
import { InvestorAuthState, investorAuthStateSelector } from 'store/state/domainData/selectors/investors';
import { openInvestorsLoginModal } from 'screens/Investors/utils/openInvestorsLoginModal';
import { MarketTrendTicker } from 'screens/Investors/sections/MarketTrendTicker';
import { MAX_CONTENT_WIDTH } from 'screens/Investors/HomePage/v2/const';
import { AvatarMenu } from './AvatarMenu';
import { HeaderSearch } from './HeaderSearch';
import { SavesButtonConnected } from './SavesButton';
import {
  ActionsZone, CenterSlot, HeaderLoginButton, LogoZone, MobileCancelButton, MobileIconButton,
  MobileLogoSlot, MobileSearchRow, MobileSearchSlot, TickerStickyWrapper, Wrapper,
} from './styled';

// the Search page already has its own filters-row search (Route.InvestorsSearchPage) — the
// header's own search is omitted there to avoid two search bars, per explicit decision
const ROUTES_WITHOUT_HEADER_SEARCH = new Set([ Route.InvestorsSearchPage ]);

interface InvestorsHeaderV2Props {
  routeName: Route;
  authState: InvestorAuthState;
  openAuthModal: () => void;
}

export const InvestorsHeaderV2Base: React.FC<InvestorsHeaderV2Props> = ({ routeName, authState, openAuthModal }) => {
  const { t } = useLocale();
  const [ isMobileSearchOpen, setIsMobileSearchOpen ] = useState(false);
  const showSearch = !ROUTES_WITHOUT_HEADER_SEARCH.has(routeName);
  // confirmed via Figma (11189:110077/11704:167343): the ticker is a second row directly
  // attached to the header, home-page-only — not a page-body section
  const showTicker = routeName === Route.InvestorsHomePage;
  // visitor: the avatar + saves cluster is replaced by a single login/register CTA (both breakpoints)
  const isVisitor = authState === 'logged-out';

  return (
    <>
    <Wrapper data-auto="investors_home_v2_header">
      <ShowAt from={3}>
        {/* RTL: first DOM child lands physically RIGHT — logo first (→ right), actions last
            (→ left). Within actions, saves is first (→ right of group), avatar last (→ left of
            group), so L→R reads [avatar][saves] with the whole group on the header's left. */}
        <LogoZone>
          <Logo route={Route.InvestorsHomePage} />
        </LogoZone>
        {showSearch ? (
          <CenterSlot>
            <HeaderSearch />
          </CenterSlot>
        ) : null}
        <ActionsZone>
          {isVisitor ? (
            <HeaderLoginButton data-auto="investors_home_v2_header_login_button" onClick={() => openAuthModal()}>
              {t('investors.home.v2.header.loginButton')}
            </HeaderLoginButton>
          ) : (
            <>
              <SavesButtonConnected />
              <AvatarMenu />
            </>
          )}
        </ActionsZone>
      </ShowAt>
      <ShowAt to={2}>
        {isMobileSearchOpen && showSearch ? (
          // RTL: search field first (→ physical right), cancel second (→ physical left) — confirmed
          // via Figma's Variant2/Variant3 symbols (11164:94176/94262): their own plain-LTR JSX
          // renders [cancel, pill] in that order, which lands cancel physically left in Figma's own
          // LTR preview — mirrored (not copied) here so the real dir="rtl" app lands on the same side.
          <MobileSearchRow>
            <MobileSearchSlot>
              <HeaderSearch autoFocus onClose={() => setIsMobileSearchOpen(false)} />
            </MobileSearchSlot>
            <MobileCancelButton data-auto="investors_home_v2_header_mobile_search_cancel" onClick={() => setIsMobileSearchOpen(false)}>
              {t('autocompleteInput.cancel')}
            </MobileCancelButton>
          </MobileSearchRow>
        ) : (
          <>
            {/* RTL: search-icon first (→ physical right), avatar last (→ physical left) */}
            <ActionsZone>
              {showSearch ? (
                <MobileIconButton data-auto="investors_home_v2_header_mobile_search_open" aria-label={t('investors.home.v2.header.mobileSearchButton')} onClick={() => setIsMobileSearchOpen(true)}>
                  <SearchThinIcon aria-hidden />
                </MobileIconButton>
              ) : null}
            </ActionsZone>
            <MobileLogoSlot>
              <Logo route={Route.InvestorsHomePage} />
            </MobileLogoSlot>
            <ActionsZone>
              {isVisitor ? (
                // reuses the same icon button the logged-in trigger uses (MobileIconButton +
                // UserIcon, confirmed via Figma node 11164:94117) — same look, opens the login
                // modal instead of the avatar menu since a visitor has no menu to open
                <MobileIconButton data-auto="investors_home_v2_header_mobile_login_button" aria-label={t('investors.home.v2.header.loginButton')} onClick={() => openAuthModal()}>
                  <UserIcon aria-hidden />
                </MobileIconButton>
              ) : (
                <AvatarMenu />
              )}
            </ActionsZone>
          </>
        )}
      </ShowAt>
    </Wrapper>
    {showTicker ? (
      <TickerStickyWrapper>
        <MarketTrendTicker sticky={false} contentWidth={MAX_CONTENT_WIDTH} />
      </TickerStickyWrapper>
    ) : null}
    </>
  );
};

const mapStateToProps = (state: State) => ({
  routeName: routeNameSelector(state),
  authState: investorAuthStateSelector(state),
});

const mapDispatchToProps = {
  openAuthModal: openInvestorsLoginModal,
};

export const InvestorsHeaderV2 = connect(mapStateToProps, mapDispatchToProps)(InvestorsHeaderV2Base);
